import React, { useState, useMemo, useCallback, useDeferredValue } from 'react';
import {
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import DropzoneArea from '../DropzoneArea';
import XtramModal, {
    XtramBadge,
    XtramButton,
    XtramCheckbox,
    XtramField,
    XtramInput,
    XtramNotice,
    XtramSearch,
    XtramSelect,
    XtramStat,
    XtramTable,
    xtramStyles as styles,
} from '../../../Widgets/XtramModal/XtramModal';
import Constants from '../../../../assests/Constants/constants';
import summaryStyles from './BmkvdJournalImportModal.module.scss';

/** How the two legs of an entry were paired out of the single-sided B100 movements. */
const formatPairingMode = (pairingMode, t) => {
    // A refund is reported as the way its legs were found plus the rewrite, e.g. "Transaction+Refund".
    const [mode, ...rest] = String(pairingMode ?? '').trim().split('+');

    const label = (() => {
        switch (mode) {
            case 'Transaction':
                return t('BmkvdPairedByTransaction');
            case 'Batch':
                return t('BmkvdPairedByBatch');
            case 'CounterAccount':
                return t('BmkvdPairedByCounterAccount');
            case 'OneSided':
                return t('BmkvdOneSided');
            case 'GeneralParty':
                return t('BmkvdGeneralParty');
            default:
                return mode || '';
        }
    })();

    return rest.includes('Refund') ? `${label} + ${t('BmkvdRefund')}` : label;
};

/** The uniform file has no sortcodes, so the VAT deduction % is inferred — snap it to a real option. */
const snapVatToOption = (vat) => {
    const v = Number(vat);
    if (Number.isNaN(v)) return null;
    const options = Constants.VATPercentage ?? [];
    if (!options.length) return v;
    return options.reduce(
        (best, opt) => (Math.abs(opt - v) < Math.abs(best - v) ? opt : best),
        options[0]
    );
};

const normalizeBmkvdMissingSortCode = (sc) => {
    const sortCodeName = typeof sc === 'string' ? sc : (sc?.sortCodeName ?? sc?.SortCodeName ?? '');
    const idFromApi = typeof sc === 'string' ? '' : String(sc?.sortCodeId ?? sc?.SortCodeId ?? '').trim();

    const vatType = sc?.sortCodeVatType ?? sc?.SortCodeVatType;
    const vat = typeof sc?.vat === 'number' ? sc.vat : typeof sc?.Vat === 'number' ? sc.Vat : null;
    const par = sc?.participatesInProfitAndLoss ?? sc?.ParticipatesInProfitAndLoss;

    return {
        sortCodeId: /^\d+$/.test(idFromApi) ? idFromApi : (sortCodeName || idFromApi),
        sortCodeName: sortCodeName || idFromApi,
        sortCodeVatType:
            vatType !== null && vatType !== undefined && vatType !== ''
                ? String(vatType).trim()
                : null,
        sortCodeCategory: sc?.sortCodeCategory ?? sc?.SortCodeCategory ?? null,
        participatesInProfitAndLoss: typeof par === 'boolean' ? par : true,
        vat: vat === null ? null : snapVatToOption(vat),
        movementCount: sc?.movementCount ?? sc?.MovementCount ?? 0,
        sampleAccounts: sc?.sampleAccounts ?? sc?.SampleAccounts ?? '',
    };
};

const BmkvdJournalImportModal = ({
    open,
    onClose,
    previewBmkvdJournal,
    commitBmkvdJournal,
    createMissingSortCodes,
    createMissingPalCode,
    direction,
    t,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    palCodeCategoryOptions = [],
    palCodes = [],
    sortCodeIdOptions = [],
    user = {},
}) => {
    const existingSortCodeOptions = useMemo(
        () => (sortCodeIdOptions || []).map(sc => ({
            value: String(sc.sortCodeId),
            label: `${sc.sortCodeId} - ${sc.sortCodeName ?? ''}`,
        })),
        [sortCodeIdOptions]
    );

    const participationOptions = useMemo(
        () => [{ label: t('Yes'), value: true }, { label: t('No'), value: false }],
        [t]
    );

    const vatPercentageOptions = useMemo(
        () => Constants.VATPercentage.map((v) => ({ label: `${v}%`, value: v })),
        []
    );

    const renderPicker = ({ options, value, onChange, placeholder, multiple = false, error = false }) => (
        <XtramSelect
            direction={direction}
            options={options}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            multiple={multiple}
            error={error}
        />
    );

    const renderFilterSelect = ({ label, options, value, onChange, isMulti = true }) => (
        <XtramField label={label}>
            {renderPicker({
                options,
                value,
                multiple: isMulti,
                onChange: (next) => {
                    onChange(next);
                    setEntriesPage(0);
                },
            })}
        </XtramField>
    );

    const [tab, setTab] = useState('file');
    const [journalFile, setJournalFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    // A uniform file carries the client's whole year, so the range scopes what is read rather than
    // just what is shown: the totals, the missing sort codes and the account cards to create all
    // come back for this period alone, and the commit runs with the same range.
    const [documentDateFrom, setDocumentDateFrom] = useState('');
    const [documentDateTo, setDocumentDateTo] = useState('');

    // The range the preview on screen was produced with. Changing a date afterwards leaves the two
    // out of step, and importing then would post a different period than the one reviewed.
    const [previewedRange, setPreviewedRange] = useState(null);

    // Whether to leave out entries the case already holds. It is only ever measured against what is
    // already saved, so a file that lists the same document twice imports it twice either way; this
    // is for the second run over a file whose entries are partly in already. Turn it off to post the
    // file as it stands and let the case hold both copies.
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const [previewedSkipDuplicates, setPreviewedSkipDuplicates] = useState(true);

    const [entries, setEntries] = useState([]);
    const [missingAccountCards, setMissingAccountCards] = useState([]);
    const [missingPalCodes, setMissingPalCodes] = useState([]);

    // Creating the main accounts the file's trial-balance groups need, mirroring the Excel
    // account-cards import. A group created here is matched by name on commit, so the cards that
    // reference it stop being created without a main account.
    const [editingPalCode, setEditingPalCode] = useState(null);
    const [palCodeBulkMode, setPalCodeBulkMode] = useState(false);
    const [selectedPalCodesForBulk, setSelectedPalCodesForBulk] = useState([]);
    const [createdPalCodes, setCreatedPalCodes] = useState([]);
    const [palCodeForm, setPalCodeForm] = useState({
        palCodeId: '',
        palCodeType: '',
        palCodeCategory: null,
    });
    const [palCodeBulkForm, setPalCodeBulkForm] = useState({
        palCodeType: '',
        palCodeCategory: null,
    });

    const palCodeTypeOptions = useMemo(
        () => (Constants.PalCodeType || []).map((type) => ({ label: type, value: type })),
        [],
    );

    const palCodeCategorySelectOptions = useMemo(
        () => (palCodeCategoryOptions || []).map((cat) => ({
            label: cat.palCodeCategoryId,
            value: cat.palCodeCategoryId,
        })),
        [palCodeCategoryOptions],
    );

    const calculateNextPalCodeOrderNumber = useCallback((category) => {
        const all = [...(palCodes || []), ...createdPalCodes];
        if (!category || all.length === 0) return 100;

        const maxOrderNumber = all
            .filter((pc) => pc.palCodeCategory === category)
            .reduce((max, pc) => Math.max(max, pc.orderNumber || 0), 0);

        return maxOrderNumber > 0 ? maxOrderNumber + 100 : 100;
    }, [palCodes, createdPalCodes]);

    /// Once a main account exists, the cards that referenced that trial-balance group will pick it
    /// up on commit — reflect that in the table straight away so the preview stays truthful.
    const applyCreatedPalCode = (palCodeId) => {
        setMissingPalCodes((prev) => prev.filter((name) => name !== palCodeId));
        setMissingAccountCards((prev) => prev.map((card) => (
            card.trialBalanceName === palCodeId ? { ...card, palCode: palCodeId } : card
        )));
    };

    const buildPalCodePayload = (palCodeId, form) => ({
        palCodeId,
        palCodeType: form.palCodeType,
        palCodeCategory: form.palCodeCategory,
        orderNumber: form.palCodeCategory ? calculateNextPalCodeOrderNumber(form.palCodeCategory) : 100,
        accountantId: user?.accountantId,
    });

    const handleCreatePalCode = async (palCodeName) => {
        if (!palCodeForm.palCodeId || !palCodeForm.palCodeType) return;

        try {
            setProcessing(true);
            const payload = buildPalCodePayload(palCodeForm.palCodeId, palCodeForm);
            const response = await createMissingPalCode(payload);
            if (!response?.ok) throw new Error(await response?.text?.());

            setCreatedPalCodes((prev) => [...prev, payload]);
            applyCreatedPalCode(palCodeName);
            setEditingPalCode(null);
            setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('BMKVD PalCode creation failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleBulkCreatePalCodes = async () => {
        if (!palCodeBulkForm.palCodeType || selectedPalCodesForBulk.length === 0) return;

        try {
            setProcessing(true);
            const base = palCodeBulkForm.palCodeCategory
                ? calculateNextPalCodeOrderNumber(palCodeBulkForm.palCodeCategory)
                : 100;

            const payloads = selectedPalCodesForBulk.map((name, index) => ({
                ...buildPalCodePayload(name, palCodeBulkForm),
                orderNumber: base + index * 100,
            }));

            const responses = await Promise.all(payloads.map((p) => createMissingPalCode(p)));
            const failed = responses.filter((r) => !r?.ok);
            if (failed.length) throw new Error(`${failed.length} PalCodes failed to create`);

            setCreatedPalCodes((prev) => [...prev, ...payloads]);
            selectedPalCodesForBulk.forEach(applyCreatedPalCode);
            setSelectedPalCodesForBulk([]);
            setPalCodeBulkForm({ palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('BMKVD bulk PalCode creation failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const resetPalCodeCreation = () => {
        setEditingPalCode(null);
        setPalCodeBulkMode(false);
        setSelectedPalCodesForBulk([]);
        setCreatedPalCodes([]);
        setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        setPalCodeBulkForm({ palCodeType: '', palCodeCategory: null });
    };
    const [editableMissing, setEditableMissing] = useState([]);
    const [fileStats, setFileStats] = useState(null);
    const [previewError, setPreviewError] = useState(null);

    // The reconciliations (התאמות) the file records in field 1371, and whether to recreate them as
    // card matches. On by default when the file has any, because a file that carries them was
    // reconciled by hand once already and there is no reason to make someone do it again.
    const [reconciliations, setReconciliations] = useState(null);
    const [importReconciliations, setImportReconciliations] = useState(true);
    const [reconciliationSearch, setReconciliationSearch] = useState('');
    const [reconciliationsPage, setReconciliationsPage] = useState(0);
    const [reconciliationsRowsPerPage, setReconciliationsRowsPerPage] = useState(10);
    const [showSkippedReconciliations, setShowSkippedReconciliations] = useState(false);

    // Transaction type -> existing sort code id. A type here is not created; its entries are posted
    // to the sort code the user picked, which is the way to correct an inferred type.
    const [sortCodeAssignments, setSortCodeAssignments] = useState({});

    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkVatType, setBulkVatType] = useState(null);
    const [bulkGroup, setBulkGroup] = useState(null);
    const [bulkPar, setBulkPar] = useState(null);
    const [bulkVat, setBulkVat] = useState(null);
    const [bulkFieldsChanged, setBulkFieldsChanged] = useState({
        vatType: false,
        group: false,
        par: false,
        vat: false
    });

    const [entriesPage, setEntriesPage] = useState(0);
    const [entriesRowsPerPage, setEntriesRowsPerPage] = useState(25);
    const [entriesOrderBy, setEntriesOrderBy] = useState('lineNumber');
    const [entriesOrder, setEntriesOrder] = useState('asc');
    const [entriesSearch, setEntriesSearch] = useState('');

    // A uniform file yields thousands of entries. Filtering against the typed value on every
    // keystroke blocks the input, so the field updates immediately and the table catches up.
    const deferredSearch = useDeferredValue(entriesSearch);

    const [filterTransactionTypes, setFilterTransactionTypes] = useState([]);
    const [filterSortCodeIds, setFilterSortCodeIds] = useState([]);
    const [filterDebitAccounts, setFilterDebitAccounts] = useState([]);
    const [filterCreditAccounts, setFilterCreditAccounts] = useState([]);
    const [filterPairingModes, setFilterPairingModes] = useState([]);
    const [filterStatus, setFilterStatus] = useState(null);

    const resetFilters = () => {
        setFilterTransactionTypes([]);
        setFilterSortCodeIds([]);
        setFilterDebitAccounts([]);
        setFilterCreditAccounts([]);
        setFilterPairingModes([]);
        setFilterStatus(null);
    };

    const isUnsupportedBmkvdFile = (file) => {
        if (!file?.name) return false;
        const name = file.name.toLowerCase();
        return name.endsWith('.csv') || name.endsWith('.xlsx') || name.endsWith('.xls')
            || name.endsWith('.pdf') || name.endsWith('.mdb');
    };

    const handleJournalFileDrop = files => {
        if (!files?.length) return;
        const file = files[0];
        setJournalFile(file);
        setPreviewDone(false);
        setPreviewError(null);
        setPreviewedRange(null);
        setEntriesPage(0);
        setEntriesSearch('');
        setTab('file');
        if (isUnsupportedBmkvdFile(file)) {
            setPreviewError(t('BmkvdWrongFileType'));
        }
    };

    const dateRange = useMemo(() => ({
        documentDateFrom: documentDateFrom || null,
        documentDateTo: documentDateTo || null,
    }), [documentDateFrom, documentDateTo]);

    const isRangeInvalid = Boolean(documentDateFrom && documentDateTo && documentDateFrom > documentDateTo);

    const isPreviewStale = Boolean(
        previewDone && previewedRange &&
        (previewedRange.documentDateFrom !== dateRange.documentDateFrom ||
            previewedRange.documentDateTo !== dateRange.documentDateTo ||
            previewedSkipDuplicates !== skipDuplicates)
    );

    const handlePreview = async () => {
        if (!journalFile) return;

        if (isUnsupportedBmkvdFile(journalFile)) {
            setPreviewError(t('BmkvdWrongFileType'));
            return;
        }

        if (isRangeInvalid) {
            setPreviewError(t('BmkvdDateRangeInvalid'));
            return;
        }

        try {
            setProcessing(true);
            setPreviewDone(false);
            setPreviewError(null);

            const res = await previewBmkvdJournal(journalFile, dateRange, skipDuplicates);

            const journalEntries = res?.entries ?? [];
            const missingSortCodes = res?.missingSortCodes ?? [];
            const missingCards = res?.missingAccountCards ?? [];

            if (!journalEntries.length) {
                const hasRange = Boolean(dateRange.documentDateFrom || dateRange.documentDateTo);
                setPreviewError(
                    hasRange
                        ? t('BmkvdDateRangeEmpty')
                        : (res?.errorMessages?.[0] || t('BmkvdPreviewEmpty'))
                );
                setPreviewDone(false);
                return;
            }

            setEntries(journalEntries);
            setMissingAccountCards(missingCards);
            setMissingPalCodes(res?.missingPalCodes ?? []);
            setFileStats({
                vatId: res?.vatId ?? '',
                movementCount: res?.movementCount ?? 0,
                accountCount: res?.accountCount ?? 0,
                zeroAmountMovementCount: res?.zeroAmountMovementCount ?? 0,
                reversedMovementCount: res?.reversedMovementCount ?? 0,
                transactionCount: res?.transactionCount ?? 0,
                batchCount: res?.batchCount ?? 0,
                balancedByTransactionCount: res?.balancedByTransactionCount ?? 0,
                balancedByBatchCount: res?.balancedByBatchCount ?? 0,
                unbalancedGroupCount: res?.unbalancedGroupCount ?? 0,
                totalDebit: res?.totalDebit ?? 0,
                totalCredit: res?.totalCredit ?? 0,
                isBalanced: !!res?.isBalanced,
                dateFrom: res?.dateFrom ?? '',
                dateTo: res?.dateTo ?? '',
                parsedRows: res?.parsedRows ?? journalEntries.length,
                validCount: res?.validCount ?? 0,
                errorCount: res?.errorCount ?? 0,
                duplicateCount: res?.duplicateCount ?? 0,
                conflictingCardIdCount: res?.conflictingCardIdCount ?? 0,
                fallbackSortCodeCount: res?.fallbackSortCodeCount ?? 0,
                generalVatSortCodeCount: res?.generalVatSortCodeCount ?? 0,
                detectedVatAccounts: res?.detectedVatAccounts ?? [],
                collapsedVatLegCount: res?.collapsedVatLegCount ?? 0,
                oneSidedEntryCount: res?.oneSidedEntryCount ?? 0,
                partylessDocumentCount: res?.partylessDocumentCount ?? 0,
                vatAccountRoles: res?.vatAccountRoles ?? [],
                refundCount: res?.refundCount ?? 0,
            });

            setReconciliations(res?.reconciliations ?? null);
            setImportReconciliations(Boolean(res?.reconciliations?.importableCount));
            setReconciliationSearch('');
            setReconciliationsPage(0);
            setShowSkippedReconciliations(false);

            setEditableMissing(missingSortCodes.map(normalizeBmkvdMissingSortCode));
            setSortCodeAssignments({});

            setEntriesPage(0);
            setEntriesSearch('');
            resetFilters();
            resetPalCodeCreation();
            setEntriesOrderBy('lineNumber');
            setEntriesOrder('asc');
            setPreviewedRange(dateRange);
            setPreviewedSkipDuplicates(skipDuplicates);
            setPreviewDone(true);
            setTab('summary');
        } catch (err) {
            console.error('BMKVD journal preview failed:', err);
            setPreviewDone(false);
            setPreviewError(err?.message || t('BmkvdPreviewFailed'));
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!journalFile || isPreviewStale) return;

        try {
            setProcessing(true);

            if (sortCodesToCreate.length > 0) {
                await createMissingSortCodes(sortCodesToCreate);
            }

            // Only ask for the reconciliations when the preview found some to create, so a file
            // without them does not come back reporting that none were imported.
            const withReconciliations = Boolean(importReconciliations && reconciliations?.importableCount);

            const res = await commitBmkvdJournal(
                journalFile, sortCodeAssignments, previewedRange ?? dateRange, withReconciliations,
                skipDuplicates);

            const errorCount = res?.errorCount ?? res?.item2 ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.item3 ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Import errors:', errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('BMKVD journal commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const updateMissing = (index, field, value) =>
        setEditableMissing(prev => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });

    const applyBulk = () => {
        setEditableMissing(prev =>
            prev.map(r =>
                selectedRows.includes(r.sortCodeId)
                    ? {
                        ...r,
                        sortCodeVatType: bulkFieldsChanged.vatType ? (bulkVatType?.value ?? null) : r.sortCodeVatType,
                        sortCodeCategory: bulkFieldsChanged.group ? (bulkGroup?.value ?? null) : r.sortCodeCategory,
                        participatesInProfitAndLoss: bulkFieldsChanged.par ? (bulkPar?.value ?? null) : r.participatesInProfitAndLoss,
                        vat: bulkFieldsChanged.vat ? (bulkVat?.value ?? null) : r.vat,
                    }
                    : r
            )
        );
        setSelectedRows([]);
        setBulkVatType(null);
        setBulkGroup(null);
        setBulkPar(null);
        setBulkVat(null);
        setBulkFieldsChanged({ vatType: false, group: false, par: false, vat: false });
    };

    // A type assigned to an existing sort code is not going to be created, so its own fields are
    // irrelevant and must not hold the commit back.
    const sortCodesToCreate = useMemo(
        () => editableMissing.filter(sc => !sortCodeAssignments[sc.sortCodeName]),
        [editableMissing, sortCodeAssignments]
    );

    const missingIncomplete = useMemo(
        () => sortCodesToCreate.some(sc =>
            !sc.sortCodeName ||
            !sc.sortCodeName.trim() ||
            sc.sortCodeVatType === null ||
            sc.sortCodeVatType === undefined ||
            sc.participatesInProfitAndLoss === null ||
            sc.participatesInProfitAndLoss === undefined ||
            sc.vat === null ||
            sc.vat === undefined
        ),
        [sortCodesToCreate]
    );

    const previewSummaries = useMemo(() => {
        const totalEntries = entries.length;
        const oneSided = entries.filter((e) => e.pairingMode === 'OneSided').length;
        const duplicates = entries.filter((e) => e.isDuplicate).length;
        const withProblems = entries.filter((e) => !e.isValid && !e.isDuplicate).length;
        const sumAmount = entries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

        const uniqueAccounts = new Set(
            entries.flatMap((e) => [e.debitAccount, e.creditAccount]).filter(Boolean)
        ).size;

        const typeMap = new Map();
        entries.forEach((e) => {
            const key = String(e.transactionType ?? '').trim() || t('BmkvdNoTransactionType');
            if (!typeMap.has(key)) {
                typeMap.set(key, { transactionType: key, count: 0, amount: 0, sortCodeId: e.sortCodeId ?? '' });
            }
            const row = typeMap.get(key);
            row.count += 1;
            row.amount += Number(e.amount) || 0;
            if (!row.sortCodeId && e.sortCodeId) row.sortCodeId = e.sortCodeId;
        });

        return {
            totalEntries,
            oneSided,
            duplicates,
            withProblems,
            sumAmount,
            uniqueAccounts,
            typeBreakdown: [...typeMap.values()].sort((a, b) => b.count - a.count),
            missingSortCodes: editableMissing.length,
            cardsToAutoCreate: missingAccountCards.length,
        };
    }, [entries, editableMissing.length, missingAccountCards.length, t]);

    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return '';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yy = String(d.getFullYear()).slice(-2);
            return `${dd}/${mm}/${yy}`;
        } catch {
            return '';
        }
    };

    const formatAmount = (n) => {
        if (n === null || n === undefined) return '';
        const num = typeof n === 'number' ? n : parseFloat(n);
        if (isNaN(num)) return '';
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Built once per preview rather than per keystroke — this is what keeps the search usable.
    const indexedEntries = useMemo(() => entries.map((e) => ({
        entry: e,
        search: [
            e.lineNumber,
            formatDate(e.documentDate),
            e.reference,
            e.reference2,
            e.transactionNumber,
            e.batchNumber,
            e.debitAccount,
            e.debitAccountName,
            e.creditAccount,
            e.creditAccountName,
            e.amount,
            e.description,
            e.transactionType,
            e.sortCodeId,
            e.sortCodeName,
            formatPairingMode(e.pairingMode, t),
        ]
            .filter((v) => v !== null && v !== undefined && v !== '')
            .join(' ')
            .toLowerCase(),
    })), [entries, t]);

    const transactionTypeOptions = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => set.add(String(e.transactionType ?? '').trim()));
        return [...set]
            .map((value) => ({ value, label: value || t('BmkvdNoTransactionType') }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    }, [entries, t]);

    const sortCodeFilterOptions = useMemo(() => {
        const map = new Map();
        entries.forEach((e) => {
            const id = e.sortCodeId || '';
            const name = e.sortCodeName || '';
            const key = id || name;
            if (!key || map.has(key)) return;
            map.set(key, { value: key, label: id && name ? `${id} - ${name}` : (id || name) });
        });
        return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    }, [entries]);

    const debitAccountOptions = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => { if (e.debitAccount) set.add(e.debitAccount); });
        return [...set]
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            .map((value) => ({ value, label: value }));
    }, [entries]);

    const creditAccountOptions = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => { if (e.creditAccount) set.add(e.creditAccount); });
        return [...set]
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            .map((value) => ({ value, label: value }));
    }, [entries]);

    const pairingModeOptions = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => { if (e.pairingMode) set.add(e.pairingMode); });
        return [...set].map((value) => ({ value, label: formatPairingMode(value, t) }));
    }, [entries, t]);

    const statusFilterOptions = useMemo(() => [
        { value: 'valid', label: t('Valid') || 'Valid' },
        { value: 'duplicate', label: t('Duplicate') || 'Duplicate' },
        { value: 'error', label: t('Errors') || 'Error' },
    ], [t]);

    const columnFilteredEntries = useMemo(() => {
        const noFilters = !filterTransactionTypes.length && !filterSortCodeIds.length
            && !filterDebitAccounts.length && !filterCreditAccounts.length
            && !filterPairingModes.length && !filterStatus;
        if (noFilters) return indexedEntries;

        const types = new Set(filterTransactionTypes.map((o) => o.value));
        const sortCodes = new Set(filterSortCodeIds.map((o) => o.value));
        const debits = new Set(filterDebitAccounts.map((o) => o.value));
        const credits = new Set(filterCreditAccounts.map((o) => o.value));
        const pairings = new Set(filterPairingModes.map((o) => o.value));

        return indexedEntries.filter(({ entry }) => {
            if (types.size && !types.has(String(entry.transactionType ?? '').trim())) return false;
            if (sortCodes.size && !sortCodes.has(entry.sortCodeId || entry.sortCodeName || '')) return false;
            if (debits.size && !debits.has(entry.debitAccount)) return false;
            if (credits.size && !credits.has(entry.creditAccount)) return false;
            if (pairings.size && !pairings.has(entry.pairingMode)) return false;
            if (filterStatus?.value === 'valid' && !(entry.isValid && !entry.isDuplicate)) return false;
            if (filterStatus?.value === 'duplicate' && !entry.isDuplicate) return false;
            if (filterStatus?.value === 'error' && !(!entry.isValid && !entry.isDuplicate)) return false;
            return true;
        });
    }, [indexedEntries, filterTransactionTypes, filterSortCodeIds, filterDebitAccounts,
        filterCreditAccounts, filterPairingModes, filterStatus]);

    const filteredEntries = useMemo(() => {
        const q = deferredSearch.trim().toLowerCase();
        const base = q ? columnFilteredEntries.filter((r) => r.search.includes(q)) : columnFilteredEntries;
        return base.map((r) => r.entry);
    }, [columnFilteredEntries, deferredSearch]);

    const hasActiveFilters = filterTransactionTypes.length > 0 || filterSortCodeIds.length > 0
        || filterDebitAccounts.length > 0 || filterCreditAccounts.length > 0
        || filterPairingModes.length > 0 || !!filterStatus || entriesSearch.trim().length > 0;

    const getEntryComparator = useCallback((order, orderBy) => (a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));
        if (order === 'asc') {
            if (isNum(aVal) && isNum(bVal)) return Number(aVal) - Number(bVal);
            return String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
        }
        if (isNum(aVal) && isNum(bVal)) return Number(bVal) - Number(aVal);
        return String(bVal ?? '').localeCompare(String(aVal ?? ''), undefined, { numeric: true });
    }, []);

    const sortedEntries = useMemo(() => {
        if (!filteredEntries.length) return [];
        return [...filteredEntries].sort(getEntryComparator(entriesOrder, entriesOrderBy));
    }, [filteredEntries, entriesOrder, entriesOrderBy, getEntryComparator]);

    const paginatedEntries = useMemo(
        () => sortedEntries.slice(
            entriesPage * entriesRowsPerPage,
            entriesPage * entriesRowsPerPage + entriesRowsPerPage
        ),
        [sortedEntries, entriesPage, entriesRowsPerPage]
    );

    const handleEntriesSort = (property) => {
        const isAsc = entriesOrderBy === property && entriesOrder === 'asc';
        setEntriesOrder(isAsc ? 'desc' : 'asc');
        setEntriesOrderBy(property);
        setEntriesPage(0);
    };

    /**
     * The reconciliations the panel lists, narrowed by the search box. A file brings over a thousand,
     * so the search is how a particular supplier or a particular file number gets found.
     */
    const filteredReconciliations = useMemo(() => {
        const items = reconciliations?.items ?? [];
        const needle = reconciliationSearch.trim().toLowerCase();
        if (!needle) return items;

        return items.filter((item) => [
            item.fileNumber,
            item.accountCode,
            item.accountName,
            item.cardId,
            item.transactionDate,
            ...(item.legs ?? []).map((leg) => String(leg.lineNumber)),
        ].some((field) => String(field ?? '').toLowerCase().includes(needle)));
    }, [reconciliations, reconciliationSearch]);

    const pagedReconciliations = useMemo(() => {
        const start = reconciliationsPage * reconciliationsRowsPerPage;
        return filteredReconciliations.slice(start, start + reconciliationsRowsPerPage);
    }, [filteredReconciliations, reconciliationsPage, reconciliationsRowsPerPage]);

    const handleClose = () => {
        setJournalFile(null);
        setPreviewDone(false);
        setEntries([]);
        setMissingAccountCards([]);
        setMissingPalCodes([]);
        setEditableMissing([]);
        setSortCodeAssignments({});
        setFileStats(null);
        setSelectedRows([]);
        setPreviewError(null);
        setEntriesPage(0);
        setEntriesSearch('');
        setDocumentDateFrom('');
        setDocumentDateTo('');
        setPreviewedRange(null);
        setSkipDuplicates(true);
        setPreviewedSkipDuplicates(true);
        setReconciliations(null);
        setImportReconciliations(true);
        setReconciliationSearch('');
        setReconciliationsPage(0);
        setShowSkippedReconciliations(false);
        resetFilters();
        resetPalCodeCreation();
        setTab('file');
        onClose();
    };

    if (!open) return null;

    // A duplicate is only marked out when it is being left behind.
    const entryRowClass = (e) => {
        if (e.isDuplicate && skipDuplicates) return styles.rowWarning;
        return e.isValid ? undefined : styles.rowError;
    };

    const renderSortHeader = (id, label, numeric = false) => (
        <TableCell className={numeric ? styles.numCell : undefined}>
            <TableSortLabel
                active={entriesOrderBy === id}
                direction={entriesOrderBy === id ? entriesOrder : 'asc'}
                onClick={() => handleEntriesSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const renderFileTab = () => (
        <>
            <div className={styles.section}>
                <p className={styles.muted}>{t('BmkvdFileFormat')}</p>
                <p className={styles.fieldHint}>{t('BmkvdFileFormatHelp')}</p>
            </div>

            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>{t('JournalEntriesFile')}</h3>
                <DropzoneArea
                    key={journalFile ? journalFile.name : 'bmkvd-journal-empty'}
                    onDrop={handleJournalFileDrop}
                    filesLimit={1}
                />
                {journalFile && <p className={styles.fileName}>{t('FileSelected')}: {journalFile.name}</p>}
            </div>

            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>{t('BmkvdDateRange')}</h3>
                <p className={styles.muted}>{t('BmkvdDateRangeHelp')}</p>
                <div className={styles.dateRow}>
                    <XtramField label={t('DocumentDateFrom')} className={styles.dateField}>
                        <XtramInput
                            direction={direction}
                            type="date"
                            value={documentDateFrom}
                            onChange={(e) => setDocumentDateFrom(e.target.value)}
                            error={isRangeInvalid}
                        />
                    </XtramField>
                    <XtramField label={t('DocumentDateTo')} className={styles.dateField}>
                        <XtramInput
                            direction={direction}
                            type="date"
                            value={documentDateTo}
                            onChange={(e) => setDocumentDateTo(e.target.value)}
                            error={isRangeInvalid}
                        />
                    </XtramField>
                    {(documentDateFrom || documentDateTo) && (
                        <XtramButton variant="ghost" onClick={() => { setDocumentDateFrom(''); setDocumentDateTo(''); }}>
                            {t('ClearFilters')}
                        </XtramButton>
                    )}
                </div>
                {isRangeInvalid && <p className={styles.fieldError}>{t('BmkvdDateRangeInvalid')}</p>}
            </div>

            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>{t('BmkvdDuplicates')}</h3>
                <XtramCheckbox checked={skipDuplicates} onChange={setSkipDuplicates} label={t('BmkvdSkipDuplicatesLabel')} />
                <p className={styles.muted}>{t('BmkvdSkipDuplicatesHelp')}</p>
            </div>

            {previewError && <XtramNotice type="error">{previewError}</XtramNotice>}
        </>
    );

    const VALUE_TONES = {
        success: summaryStyles.valueSuccess,
        warning: summaryStyles.valueWarning,
        error: summaryStyles.valueError,
        muted: summaryStyles.valueMuted,
    };

    /** Counts that only matter when they are non-zero read as muted at zero and take the tone otherwise. */
    const countTone = (count, tone) => (count > 0 ? tone : 'muted');

    const renderSummaryPanel = (title, rows, badge = null) => (
        <div className={summaryStyles.summaryPanel}>
            <div className={summaryStyles.summaryPanelHeader}>
                <span>{title}</span>
                {badge}
            </div>
            <table className={summaryStyles.summaryTable}>
                <tbody>
                    {rows.filter((row) => !row.hidden).map((row) => (
                        <tr key={row.label}>
                            <th scope="row">{row.label}</th>
                            <td className={VALUE_TONES[row.tone]}>{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderSummaryTab = () => {
        const difference = (Number(fileStats.totalDebit) || 0) - (Number(fileStats.totalCredit) || 0);
        const hasDifference = Math.abs(difference) >= 0.005;

        return (
            <>
                <div className={styles.section}>
                    <div className={summaryStyles.summaryGrid}>
                        {renderSummaryPanel(t('BmkvdSummaryFileSection'), [
                            { label: t('TaxId'), value: fileStats.vatId, hidden: !fileStats.vatId },
                            { label: t('DateRange'), value: `${formatDate(fileStats.dateFrom)} - ${formatDate(fileStats.dateTo)}` },
                            { label: t('BmkvdMovements'), value: fileStats.movementCount },
                            { label: t('BmkvdAccountsInFile'), value: fileStats.accountCount },
                            { label: t('BmkvdTransactions'), value: fileStats.transactionCount },
                            { label: t('BmkvdBatches'), value: fileStats.batchCount },
                            {
                                label: t('BmkvdReversedMovements'),
                                value: fileStats.reversedMovementCount,
                                hidden: !(fileStats.reversedMovementCount > 0),
                            },
                        ])}

                        {renderSummaryPanel(t('BmkvdSummaryEntriesSection'), [
                            { label: t('JournalEntries'), value: previewSummaries.totalEntries },
                            { label: t('BmkvdPairedByTransaction'), value: fileStats.balancedByTransactionCount },
                            { label: t('BmkvdPairedByBatch'), value: fileStats.balancedByBatchCount },
                            {
                                label: t('BmkvdOneSided'),
                                value: previewSummaries.oneSided,
                                tone: countTone(previewSummaries.oneSided, 'warning'),
                            },
                            {
                                label: t('Duplicates'),
                                value: previewSummaries.duplicates,
                                tone: countTone(previewSummaries.duplicates, 'warning'),
                            },
                            {
                                label: t('ErrorCount'),
                                value: previewSummaries.withProblems,
                                tone: 'error',
                                hidden: !(previewSummaries.withProblems > 0),
                            },
                        ])}

                        {renderSummaryPanel(
                            t('BmkvdSummaryBalanceSection'),
                            [
                                { label: t('TotalDebit'), value: formatAmount(fileStats.totalDebit) },
                                { label: t('TotalCredit'), value: formatAmount(fileStats.totalCredit) },
                                {
                                    label: t('Difference'),
                                    value: formatAmount(hasDifference ? difference : 0),
                                    tone: hasDifference ? 'error' : 'success',
                                },
                                {
                                    label: t('MissingSortCodes'),
                                    value: previewSummaries.missingSortCodes,
                                    tone: previewSummaries.missingSortCodes > 0 ? 'warning' : 'success',
                                },
                                { label: t('CardsToAutoCreate'), value: previewSummaries.cardsToAutoCreate },
                                {
                                    label: t('BmkvdConflictingCardIds'),
                                    value: fileStats.conflictingCardIdCount,
                                    tone: 'warning',
                                    hidden: !(fileStats.conflictingCardIdCount > 0),
                                },
                            ],
                            <span className={`${summaryStyles.pill} ${fileStats.isBalanced ? summaryStyles.pillSuccess : summaryStyles.pillError}`}>
                                {fileStats.isBalanced ? t('BmkvdBalanced') : t('BmkvdNotBalanced')}
                            </span>
                        )}
                    </div>

                    {previewSummaries.duplicates > 0 && (
                        <XtramNotice type={skipDuplicates ? 'warning' : 'success'}>
                            {t('Duplicates')}: <strong>{previewSummaries.duplicates}</strong>{' '}
                            ({skipDuplicates ? t('BmkvdDuplicatesSkipped') : t('BmkvdDuplicatesImported')})
                        </XtramNotice>
                    )}
                    {fileStats.generalVatSortCodeCount > 0 && (
                        <XtramNotice type="success">
                            {t('BmkvdGeneralVatSortCodeNotice', { count: fileStats.generalVatSortCodeCount })}
                        </XtramNotice>
                    )}
                    {fileStats.fallbackSortCodeCount > 0 && (
                        <XtramNotice type="warning">
                            {t('BmkvdFallbackSortCodeNotice', { count: fileStats.fallbackSortCodeCount })}
                        </XtramNotice>
                    )}
                    {fileStats.detectedVatAccounts?.length > 0 && (
                        <XtramNotice type="info">
                            <span>{t('BmkvdDetectedVatAccounts')}: <strong>{fileStats.detectedVatAccounts.join(' | ')}</strong></span>
                        </XtramNotice>
                    )}
                    {fileStats.collapsedVatLegCount > 0 && (
                        <XtramNotice type="info">
                            {t('BmkvdCollapsedVatLegsNotice', { count: fileStats.collapsedVatLegCount })}
                        </XtramNotice>
                    )}
                    {fileStats.partylessDocumentCount > 0 && (
                        <XtramNotice type="info">
                            {t('BmkvdPartylessDocumentsNotice', { count: fileStats.partylessDocumentCount })}
                        </XtramNotice>
                    )}
                    {fileStats.oneSidedEntryCount > 0 && (
                        <XtramNotice type="warning">
                            {t('BmkvdOneSidedEntriesNotice', { count: fileStats.oneSidedEntryCount })}
                        </XtramNotice>
                    )}
                    {fileStats.refundCount > 0 && (
                        <XtramNotice type="warning">
                            {t('BmkvdRefundsNotice', { count: fileStats.refundCount })}
                        </XtramNotice>
                    )}
                </div>

                {fileStats.vatAccountRoles?.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>{t('BmkvdVatAccountRoles')}</h3>
                        <p className={styles.muted}>{t('BmkvdVatAccountRolesHelp')}</p>
                        <XtramTable>
                            <Table size="small" dir={direction}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('BmkvdVatRole')}</TableCell>
                                        <TableCell>{t('BmkvdVatRoleFileAccounts')}</TableCell>
                                        <TableCell>{t('BmkvdVatRoleSystemCard')}</TableCell>
                                        <TableCell>{t('BmkvdVatRoleGeneralSortCode')}</TableCell>
                                        <TableCell className={styles.actionsCell}>{t('Quantity')}</TableCell>
                                        <TableCell className={styles.actionsCell}>{t('BmkvdVatRoleContraSide')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fileStats.vatAccountRoles.map((role) => (
                                        <TableRow key={role.sortCodeVatType}>
                                            <TableCell>{role.roleName}</TableCell>
                                            <TableCell>
                                                {role.fileAccounts?.length
                                                    ? role.fileAccounts.join(' | ')
                                                    : <span className={styles.fieldHint}>{t('BmkvdVatRoleNoFileAccount')}</span>}
                                            </TableCell>
                                            <TableCell>{[role.systemCardId, role.systemCardName].filter(Boolean).join(' - ')}</TableCell>
                                            <TableCell>
                                                {role.generalSortCodeId
                                                    ? [role.generalSortCodeId, role.generalSortCodeName].filter(Boolean).join(' - ')
                                                    : <span className={styles.textError}>{t('BmkvdVatRoleNoGeneralSortCode')}</span>}
                                            </TableCell>
                                            <TableCell className={styles.actionsCell}>{role.documentCount}</TableCell>
                                            <TableCell className={styles.actionsCell}>{role.contraSideDocumentCount}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </XtramTable>
                    </div>
                )}

                {previewSummaries.typeBreakdown.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>{t('BmkvdSummaryByTransactionType')}</h3>
                        <XtramTable height="medium">
                            <Table size="small" stickyHeader dir={direction}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('BmkvdTransactionType')}</TableCell>
                                        <TableCell>{t('SortCode Id')}</TableCell>
                                        <TableCell className={styles.actionsCell}>{t('Quantity')}</TableCell>
                                        <TableCell className={styles.numCell}>{t('Amount')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {previewSummaries.typeBreakdown.map((row) => (
                                        <TableRow key={row.transactionType}>
                                            <TableCell>{row.transactionType}</TableCell>
                                            <TableCell>{row.sortCodeId || ''}</TableCell>
                                            <TableCell className={styles.actionsCell}>{row.count}</TableCell>
                                            <TableCell className={styles.numCell}>{formatAmount(row.amount)}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow className={styles.totalRow}>
                                        <TableCell colSpan={2}>{t('Total')}</TableCell>
                                        <TableCell className={styles.actionsCell}>{previewSummaries.totalEntries}</TableCell>
                                        <TableCell className={styles.numCell}>{formatAmount(previewSummaries.sumAmount)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </XtramTable>
                    </div>
                )}
            </>
        );
    };

    const renderReconciliationsTab = () => (
        <div className={styles.section}>
            <p className={styles.muted}>{t('BmkvdReconciliationsHelp')}</p>

            {!reconciliations.isAvailable ? (
                <XtramNotice type="warning">
                    {reconciliations.unavailableReason || t('BmkvdReconciliationsNone')}
                </XtramNotice>
            ) : (
                <>
                    <div className={styles.stats}>
                        <XtramStat label={t('BmkvdReconciliationsInFile')} value={reconciliations.totalCount} />
                        <XtramStat label={t('BmkvdReconciliationsToCreate')} value={reconciliations.importableCount} tone="success" />
                        <XtramStat label={t('BmkvdReconciliationsAccounts')} value={reconciliations.accountCount} />
                        {reconciliations.skipped?.length > 0 && (
                            <XtramStat label={t('BmkvdReconciliationsSkipped')} value={reconciliations.skipped.length} tone="warning" />
                        )}
                    </div>

                    {reconciliations.bankReconciledMovementCount > 0 && (
                        <XtramNotice type="info">
                            {t('BmkvdBankReconciledNotice', { count: reconciliations.bankReconciledMovementCount })}
                        </XtramNotice>
                    )}

                    <XtramCheckbox
                        checked={importReconciliations}
                        disabled={!reconciliations.importableCount}
                        onChange={setImportReconciliations}
                        label={t('BmkvdImportReconciliations', { count: reconciliations.importableCount })}
                    />

                    {reconciliations.importableCount > 0 && (
                        <>
                            <div className={styles.formFieldWide}>
                                <XtramSearch
                                    direction={direction}
                                    value={reconciliationSearch}
                                    onChange={(v) => {
                                        setReconciliationSearch(v);
                                        setReconciliationsPage(0);
                                    }}
                                    placeholder={t('BmkvdReconciliationsSearch')}
                                />
                            </div>
                            <XtramTable height="medium">
                                <Table size="small" stickyHeader dir={direction}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('BmkvdReconciliationNumber')}</TableCell>
                                            <TableCell>{t('AccountCard')}</TableCell>
                                            <TableCell>{t('DocumentDate')}</TableCell>
                                            <TableCell className={styles.numCell}>{t('TotalDebit')}</TableCell>
                                            <TableCell className={styles.numCell}>{t('TotalCredit')}</TableCell>
                                            <TableCell>{t('BmkvdReconciliationEntries')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedReconciliations.map((item) => (
                                            <TableRow key={`${item.accountCode}-${item.fileNumber}`}>
                                                <TableCell>{item.fileNumber}</TableCell>
                                                <TableCell className={styles.wrapCell}>
                                                    {[item.cardId, item.accountName].filter(Boolean).join(' - ')}
                                                    {item.cardId !== item.accountCode && (
                                                        <span className={styles.fieldHint}> ({item.accountCode})</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>{formatDate(item.transactionDate)}</TableCell>
                                                <TableCell className={styles.numCell}>
                                                    {formatAmount(item.debitTotal)}
                                                    <span className={styles.fieldHint}> ({item.debitCount})</span>
                                                </TableCell>
                                                <TableCell className={styles.numCell}>
                                                    {formatAmount(item.creditTotal)}
                                                    <span className={styles.fieldHint}> ({item.creditCount})</span>
                                                </TableCell>
                                                <TableCell className={styles.wrapCell}>
                                                    {(item.legs ?? []).map((leg) => leg.lineNumber).join(', ')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    component="div"
                                    count={filteredReconciliations.length}
                                    page={reconciliationsPage}
                                    onPageChange={(e, page) => setReconciliationsPage(page)}
                                    rowsPerPage={reconciliationsRowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        setReconciliationsRowsPerPage(parseInt(e.target.value, 10));
                                        setReconciliationsPage(0);
                                    }}
                                    rowsPerPageOptions={[10, 25, 50]}
                                    labelRowsPerPage={t('RowsPerPage')}
                                />
                            </XtramTable>
                        </>
                    )}

                    {reconciliations.skipped?.length > 0 && (
                        <>
                            <div>
                                <XtramButton variant="ghost" small onClick={() => setShowSkippedReconciliations(v => !v)}>
                                    {showSkippedReconciliations
                                        ? t('Hide')
                                        : t('BmkvdShowSkippedReconciliations', { count: reconciliations.skipped.length })}
                                </XtramButton>
                            </div>
                            {showSkippedReconciliations && (
                                <XtramTable height="medium">
                                    <Table size="small" stickyHeader dir={direction}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t('BmkvdReconciliationNumber')}</TableCell>
                                                <TableCell>{t('AccountCard')}</TableCell>
                                                <TableCell>{t('Reason')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reconciliations.skipped.map((item) => (
                                                <TableRow key={`${item.accountCode}-${item.fileNumber}-skipped`}>
                                                    <TableCell>{item.fileNumber}</TableCell>
                                                    <TableCell>{[item.accountCode, item.accountName].filter(Boolean).join(' - ')}</TableCell>
                                                    <TableCell className={styles.wrapCell}>{item.reason}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </XtramTable>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );

    const renderMissingTab = () => {
        if (editableMissing.length === 0) {
            return <XtramNotice type="success">{t('NoMissingSortCodes') || 'אין סוגי תנועות חסרים'}</XtramNotice>;
        }

        return (
            <div className={styles.section}>
                <p className={styles.muted}>{t('BmkvdMissingSortCodesHelp')}</p>
                <p className={styles.muted}>{t('BmkvdAssignExistingSortCodeHelp')}</p>

                <div className={styles.formFields}>
                    <XtramField>
                        {renderPicker({
                            placeholder: t('SelectSortCodeVatType'),
                            options: formattedSortCodeVatType,
                            value: bulkVatType,
                            onChange: (val) => {
                                setBulkVatType(val);
                                setBulkFieldsChanged(prev => ({ ...prev, vatType: true }));
                            },
                        })}
                    </XtramField>
                    <XtramField>
                        {renderPicker({
                            placeholder: t('SelectSortCodeGroupName'),
                            options: formattedSortCodesGroupNamesOptions,
                            value: bulkGroup,
                            onChange: (val) => {
                                setBulkGroup(val);
                                setBulkFieldsChanged(prev => ({ ...prev, group: true }));
                            },
                        })}
                    </XtramField>
                    <XtramField>
                        {renderPicker({
                            placeholder: t('participatesInProfitAndLoss'),
                            options: participationOptions,
                            value: bulkPar,
                            onChange: (val) => {
                                setBulkPar(val);
                                setBulkFieldsChanged(prev => ({ ...prev, par: true }));
                            },
                        })}
                    </XtramField>
                    <XtramField size="narrow">
                        {renderPicker({
                            placeholder: t('VAT'),
                            options: vatPercentageOptions,
                            value: bulkVat,
                            onChange: (val) => {
                                setBulkVat(val);
                                setBulkFieldsChanged(prev => ({ ...prev, vat: true }));
                            },
                        })}
                    </XtramField>
                    <XtramButton
                        variant="ghost"
                        disabled={!bulkFieldsChanged.vatType && !bulkFieldsChanged.group && !bulkFieldsChanged.par && !bulkFieldsChanged.vat}
                        onClick={applyBulk}
                    >
                        {t('ApplyToSelected')}
                    </XtramButton>
                </div>

                <XtramTable height="tall">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" className={styles.actionsCell}>
                                    <Checkbox
                                        size="small"
                                        className={styles.tableCheckbox}
                                        checked={selectedRows.length === editableMissing.length && editableMissing.length > 0}
                                        onChange={e => setSelectedRows(e.target.checked ? editableMissing.map(r => r.sortCodeId) : [])}
                                    />
                                </TableCell>
                                <TableCell>{t('BmkvdAssignExistingSortCode')}</TableCell>
                                <TableCell>{t('SortCode Id')}</TableCell>
                                <TableCell>{t('SortCode Name')}</TableCell>
                                <TableCell>{t('SelectSortCodeVatType')}</TableCell>
                                <TableCell>{t('SelectSortCodeGroupName')}</TableCell>
                                <TableCell>{t('participatesInProfitAndLoss')}</TableCell>
                                <TableCell>{t('VAT')}</TableCell>
                                <TableCell className={styles.actionsCell}>{t('Quantity')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {editableMissing.map((sc, i) => {
                                const assignedTo = sortCodeAssignments[sc.sortCodeName] || null;

                                return (
                                    <TableRow key={sc.sortCodeId} className={assignedTo ? styles.rowDimmed : undefined}>
                                        <TableCell padding="checkbox" className={styles.actionsCell}>
                                            <Checkbox
                                                size="small"
                                                className={styles.tableCheckbox}
                                                checked={selectedRows.includes(sc.sortCodeId)}
                                                onChange={e => setSelectedRows(prev => e.target.checked ? [...prev, sc.sortCodeId] : prev.filter(id => id !== sc.sortCodeId))}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className={styles.cellFieldWide}>
                                                {renderPicker({
                                                    placeholder: t('BmkvdCreateNewSortCode'),
                                                    options: existingSortCodeOptions,
                                                    value: existingSortCodeOptions.find(o => o.value === assignedTo) || null,
                                                    onChange: opt => setSortCodeAssignments(prev => {
                                                        const next = { ...prev };
                                                        if (opt) next[sc.sortCodeName] = opt.value;
                                                        else delete next[sc.sortCodeName];
                                                        return next;
                                                    }),
                                                })}
                                            </div>
                                        </TableCell>
                                        <TableCell>{sc.sortCodeId}</TableCell>
                                        <TableCell>
                                            <div className={styles.cellFieldWide}>
                                                <XtramInput
                                                    direction={direction}
                                                    value={sc.sortCodeName || ''}
                                                    onChange={e => updateMissing(i, 'sortCodeName', e.target.value)}
                                                    placeholder={t('EnterSortCodeName') || 'Enter sortcode name'}
                                                    error={!sc.sortCodeName || !sc.sortCodeName.trim()}
                                                />
                                                {sc.sampleAccounts && <p className={styles.fieldHint}>{sc.sampleAccounts}</p>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className={styles.cellField}>
                                                {renderPicker({
                                                    placeholder: t('SelectSortCodeVatType'),
                                                    options: formattedSortCodeVatType,
                                                    value: formattedSortCodeVatType.find(opt => opt.value === sc.sortCodeVatType?.toString()?.trim()) || null,
                                                    onChange: opt => updateMissing(i, 'sortCodeVatType', opt?.value ?? null),
                                                    error: !assignedTo && sc.sortCodeVatType == null,
                                                })}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className={styles.cellField}>
                                                {renderPicker({
                                                    placeholder: t('SelectSortCodeGroupName'),
                                                    options: formattedSortCodesGroupNamesOptions,
                                                    value: formattedSortCodesGroupNamesOptions.find(opt => opt.value === sc.sortCodeCategory) || null,
                                                    onChange: opt => updateMissing(i, 'sortCodeCategory', opt?.value ?? null),
                                                })}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className={styles.cellField}>
                                                {renderPicker({
                                                    placeholder: t('participatesInProfitAndLoss'),
                                                    options: participationOptions,
                                                    value: participationOptions.find(opt => opt.value === sc.participatesInProfitAndLoss) || null,
                                                    onChange: opt => updateMissing(i, 'participatesInProfitAndLoss', opt?.value ?? null),
                                                    error: !assignedTo && sc.participatesInProfitAndLoss == null,
                                                })}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className={styles.cellFieldNarrow}>
                                                {renderPicker({
                                                    placeholder: t('VAT'),
                                                    options: vatPercentageOptions,
                                                    value: vatPercentageOptions.find(opt => opt.value === sc.vat) || null,
                                                    onChange: opt => updateMissing(i, 'vat', opt?.value ?? null),
                                                    error: !assignedTo && sc.vat == null,
                                                })}
                                            </div>
                                        </TableCell>
                                        <TableCell className={styles.actionsCell}>{sc.movementCount || ''}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </XtramTable>
            </div>
        );
    };

    const renderPalCodeForm = (form, setForm) => (
        <div className={styles.formFields}>
            <XtramField label={t('PalCodeType')} required>
                {renderPicker({
                    placeholder: t('Select'),
                    options: palCodeTypeOptions,
                    value: palCodeTypeOptions.find(o => o.value === form.palCodeType) || null,
                    onChange: (o) => setForm({ ...form, palCodeType: o?.value || '' }),
                    error: !form.palCodeType,
                })}
            </XtramField>
            <XtramField label={t('PalCodeCategory')}>
                {renderPicker({
                    placeholder: t('None'),
                    options: palCodeCategorySelectOptions,
                    value: palCodeCategorySelectOptions.find(o => o.value === form.palCodeCategory) || null,
                    onChange: (o) => setForm({ ...form, palCodeCategory: o?.value || null }),
                })}
            </XtramField>
        </div>
    );

    const renderCardsTab = () => {
        if (missingAccountCards.length === 0) {
            return <XtramNotice type="success">{t('CardsToAutoCreate')}: 0</XtramNotice>;
        }

        return (
            <>
                <div className={styles.section}>
                    <XtramNotice type="info">
                        <span>{t('CardsToAutoCreateHelp')}</span>
                        <span>{t('BmkvdJournalIncludesCardsHelp')}</span>
                    </XtramNotice>
                    {fileStats?.conflictingCardIdCount > 0 && (
                        <XtramNotice type="error">{t('BmkvdCardIdConflictHelp')}</XtramNotice>
                    )}
                </div>

                {missingPalCodes.length > 0 && (
                    <div className={styles.section}>
                        <div className={styles.toolbar}>
                            <div>
                                <h3 className={styles.sectionTitle}>{t('MissingPalCodes')} ({missingPalCodes.length})</h3>
                                <p className={styles.muted}>{t('BmkvdMissingPalCodesHelp')}</p>
                            </div>
                            <XtramButton
                                variant="ghost"
                                active={palCodeBulkMode}
                                onClick={() => {
                                    setPalCodeBulkMode(!palCodeBulkMode);
                                    setSelectedPalCodesForBulk([]);
                                    setEditingPalCode(null);
                                    if (!palCodeBulkMode) {
                                        setPalCodeBulkForm({ palCodeType: '', palCodeCategory: null });
                                    }
                                }}
                            >
                                {palCodeBulkMode ? t('CancelBulk') : t('BulkCreate')}
                            </XtramButton>
                        </div>

                        {palCodeBulkMode && (
                            <div className={styles.card}>
                                <h3 className={styles.sectionTitle}>{t('BulkCreatePalCodes')}</h3>
                                {renderPalCodeForm(palCodeBulkForm, setPalCodeBulkForm)}
                                <p className={styles.muted}>
                                    {t('SelectedPalCodes')}: {selectedPalCodesForBulk.length} / {missingPalCodes.length}
                                </p>
                                <div className={styles.toolbarGroup}>
                                    <XtramButton
                                        variant="secondary"
                                        small
                                        onClick={() => setSelectedPalCodesForBulk(
                                            selectedPalCodesForBulk.length === missingPalCodes.length ? [] : [...missingPalCodes],
                                        )}
                                    >
                                        {selectedPalCodesForBulk.length === missingPalCodes.length ? t('DeselectAll') : t('SelectAll')}
                                    </XtramButton>
                                    <XtramButton
                                        variant="primary"
                                        small
                                        onClick={handleBulkCreatePalCodes}
                                        disabled={processing || !palCodeBulkForm.palCodeType || selectedPalCodesForBulk.length === 0}
                                    >
                                        {t('CreateSelected')}
                                    </XtramButton>
                                </div>
                            </div>
                        )}

                        {missingPalCodes.map((palCodeName, idx) => (
                            <div key={`${palCodeName}-${idx}`} className={styles.card}>
                                {editingPalCode === palCodeName ? (
                                    <>
                                        <XtramField label={t('PalCodeId')} size="wide">
                                            <XtramInput direction={direction} value={palCodeForm.palCodeId} disabled />
                                        </XtramField>
                                        {renderPalCodeForm(palCodeForm, setPalCodeForm)}
                                        <div className={styles.toolbarGroup}>
                                            <XtramButton
                                                variant="primary"
                                                small
                                                onClick={() => handleCreatePalCode(palCodeName)}
                                                disabled={processing || !palCodeForm.palCodeType}
                                            >
                                                {t('create')}
                                            </XtramButton>
                                            <XtramButton
                                                variant="secondary"
                                                small
                                                onClick={() => {
                                                    setEditingPalCode(null);
                                                    setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
                                                }}
                                            >
                                                {t('Cancel')}
                                            </XtramButton>
                                        </div>
                                    </>
                                ) : (
                                    <div className={styles.cardRow}>
                                        <div className={styles.toolbarGroup}>
                                            {palCodeBulkMode && (
                                                <Checkbox
                                                    size="small"
                                                    className={styles.tableCheckbox}
                                                    checked={selectedPalCodesForBulk.includes(palCodeName)}
                                                    onChange={(e) => setSelectedPalCodesForBulk(prev => (e.target.checked
                                                        ? [...prev, palCodeName]
                                                        : prev.filter(pc => pc !== palCodeName)))}
                                                />
                                            )}
                                            <strong>{palCodeName}</strong>
                                            <XtramBadge>
                                                {missingAccountCards.filter(c => c.trialBalanceName === palCodeName).length}
                                            </XtramBadge>
                                        </div>
                                        {!palCodeBulkMode && (
                                            <XtramButton
                                                variant="ghost"
                                                small
                                                onClick={() => {
                                                    setEditingPalCode(palCodeName);
                                                    setPalCodeForm({ palCodeId: palCodeName, palCodeType: '', palCodeCategory: null });
                                                }}
                                            >
                                                {t('CreatePalCode')}
                                            </XtramButton>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                        {t('CardsToAutoCreate') || 'כרטיסים שייווצרו אוטומטית בעת הייבוא'} ({missingAccountCards.length})
                    </h3>
                    <XtramTable height="tall">
                        <Table size="small" stickyHeader dir={direction}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('CardId')}</TableCell>
                                    <TableCell>{t('BmkvdFileAccountCode')}</TableCell>
                                    <TableCell>{t('CardName')}</TableCell>
                                    <TableCell>{t('PalCode')}</TableCell>
                                    <TableCell className={styles.actionsCell}>{t('BmkvdMovements')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {missingAccountCards.map((card, idx) => (
                                    <TableRow
                                        key={`${card.cardId ?? idx}`}
                                        className={card.hasCardIdConflict ? styles.rowWarning : undefined}
                                        title={card.hasCardIdConflict
                                            ? `${t('BmkvdCardIdTakenBy')}: ${card.conflictingCardName ?? ''}`
                                            : ''}
                                    >
                                        <TableCell>
                                            {card.cardId || ''}
                                            {card.hasCardIdConflict && (
                                                <span className={styles.textWarning}> ({t('BmkvdNewCardId')})</span>
                                            )}
                                        </TableCell>
                                        <TableCell>{card.fileAccountCode || ''}</TableCell>
                                        <TableCell className={styles.wrapCell}>
                                            {card.cardName || card.cardId || ''}
                                            {card.hasCardIdConflict && card.conflictingCardName && (
                                                <p className={styles.fieldHint}>{t('BmkvdCardIdTakenBy')}: {card.conflictingCardName}</p>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {card.palCode || (
                                                <span className={styles.textError}>
                                                    {card.trialBalanceName
                                                        ? `${t('BmkvdPalCodeNotFound')}: ${card.trialBalanceName}`
                                                        : t('BmkvdPalCodeNotFound')}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className={styles.actionsCell}>{card.movementCount ?? ''}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </XtramTable>
                </div>
            </>
        );
    };

    const renderEntriesTab = () => (
        <div className={styles.section}>
            <div className={styles.toolbar}>
                <h3 className={styles.sectionTitle}>
                    {t('JournalEntries')} ({filteredEntries.length}{hasActiveFilters ? ` / ${entries.length}` : ''})
                </h3>
                {hasActiveFilters && (
                    <XtramButton
                        variant="ghost"
                        small
                        onClick={() => {
                            resetFilters();
                            setEntriesSearch('');
                            setEntriesPage(0);
                        }}
                    >
                        {t('ClearFilters') || 'נקה מסננים'}
                    </XtramButton>
                )}
            </div>

            <div className={styles.formFields}>
                <XtramField label={t('Search')}>
                    <XtramSearch
                        direction={direction}
                        value={entriesSearch}
                        onChange={(v) => {
                            setEntriesSearch(v);
                            setEntriesPage(0);
                        }}
                        placeholder={t('Search')}
                    />
                </XtramField>
                {renderFilterSelect({ label: t('BmkvdTransactionType'), options: transactionTypeOptions, value: filterTransactionTypes, onChange: setFilterTransactionTypes })}
                {renderFilterSelect({ label: t('SortCode Id'), options: sortCodeFilterOptions, value: filterSortCodeIds, onChange: setFilterSortCodeIds })}
                {renderFilterSelect({ label: t('DebitAccount'), options: debitAccountOptions, value: filterDebitAccounts, onChange: setFilterDebitAccounts })}
                {renderFilterSelect({ label: t('CreditAccount'), options: creditAccountOptions, value: filterCreditAccounts, onChange: setFilterCreditAccounts })}
                {renderFilterSelect({ label: t('BmkvdPairing'), options: pairingModeOptions, value: filterPairingModes, onChange: setFilterPairingModes })}
                {renderFilterSelect({ label: t('Status'), options: statusFilterOptions, value: filterStatus, onChange: setFilterStatus, isMulti: false })}
            </div>

            <XtramTable height="tall">
                <Table size="small" stickyHeader dir={direction}>
                    <TableHead>
                        <TableRow>
                            {renderSortHeader('lineNumber', '#')}
                            {renderSortHeader('documentDate', t('DocumentDate'))}
                            {renderSortHeader('reference', t('Reference'))}
                            {renderSortHeader('transactionNumber', t('BmkvdTransactionNumber'))}
                            {renderSortHeader('batchNumber', t('BmkvdBatchNumber'))}
                            {renderSortHeader('debitAccount', t('DebitAccount'))}
                            {renderSortHeader('debitAccountName', t('DebitName'))}
                            {renderSortHeader('creditAccount', t('CreditAccount'))}
                            {renderSortHeader('creditAccountName', t('CreditName'))}
                            {renderSortHeader('amount', t('Amount'), true)}
                            {renderSortHeader('description', t('Description'))}
                            {renderSortHeader('transactionType', t('BmkvdTransactionType'))}
                            {renderSortHeader('sortCodeId', t('SortCode Id'))}
                            {renderSortHeader('pairingMode', t('BmkvdPairing'))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedEntries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={14} className={styles.emptyState}>
                                    {t('importModal.noResults')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedEntries.map((e) => (
                                <TableRow
                                    key={`${e.lineNumber}-${e.transactionNumber ?? ''}-${e.amount}`}
                                    hover
                                    className={entryRowClass(e)}
                                    title={e.errorMessage || ''}
                                >
                                    <TableCell>{e.lineNumber}</TableCell>
                                    <TableCell>{formatDate(e.documentDate)}</TableCell>
                                    <TableCell>{e.reference || ''}</TableCell>
                                    <TableCell>{e.transactionNumber || ''}</TableCell>
                                    <TableCell>{e.batchNumber || ''}</TableCell>
                                    <TableCell>
                                        {e.debitAccount || <span className={styles.textError}>{t('BmkvdMissingSide')}</span>}
                                    </TableCell>
                                    <TableCell className={styles.wrapCell}>{e.debitAccountName || ''}</TableCell>
                                    <TableCell>
                                        {e.creditAccount || <span className={styles.textError}>{t('BmkvdMissingSide')}</span>}
                                    </TableCell>
                                    <TableCell className={styles.wrapCell}>{e.creditAccountName || ''}</TableCell>
                                    <TableCell className={styles.numCell}>{formatAmount(e.amount)}</TableCell>
                                    <TableCell className={styles.wrapCell}>{e.description || ''}</TableCell>
                                    <TableCell>{e.transactionType || ''}</TableCell>
                                    <TableCell>
                                        {e.sortCodeId || <span className={styles.textInfo}>{t('Auto')}</span>}
                                    </TableCell>
                                    <TableCell>{formatPairingMode(e.pairingMode, t)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={sortedEntries.length}
                    page={entriesPage}
                    onPageChange={(_, p) => setEntriesPage(p)}
                    rowsPerPage={entriesRowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setEntriesRowsPerPage(parseInt(e.target.value, 10));
                        setEntriesPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage={t('RowsPerPage')}
                />
            </XtramTable>
        </div>
    );

    const hasPreview = previewDone && !!fileStats;

    const tabs = [
        { key: 'file', label: t('importModal.fileTab') },
        { key: 'summary', label: t('Summary'), disabled: !hasPreview },
        ...(reconciliations ? [{
            key: 'reconciliations',
            label: t('BmkvdReconciliations'),
            count: hasPreview ? (reconciliations.importableCount ?? 0) : null,
            disabled: !hasPreview,
        }] : []),
        {
            key: 'missing',
            label: t('MissingSortCodes'),
            count: hasPreview ? editableMissing.length : null,
            alert: missingIncomplete,
            disabled: !hasPreview,
        },
        {
            key: 'cards',
            label: t('CardsToAutoCreate'),
            count: hasPreview ? missingAccountCards.length : null,
            alert: missingPalCodes.length > 0,
            disabled: !hasPreview,
        },
        {
            key: 'entries',
            label: t('JournalEntries'),
            count: hasPreview ? entries.length : null,
            disabled: !hasPreview,
        },
    ];

    const activeTab = hasPreview || tab === 'file' ? tab : 'file';

    return (
        <XtramModal
            open={open}
            onClose={handleClose}
            direction={direction}
            busy={processing}
            expanded={hasPreview}
            title={t('ImportBmkvdJournalEntries')}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setTab}
            footer={(
                <>
                    <XtramButton variant="secondary" onClick={handleClose} disabled={processing}>
                        {t('cancelButton')}
                    </XtramButton>
                    <XtramButton
                        variant="ghost"
                        onClick={handlePreview}
                        disabled={!journalFile || processing || isRangeInvalid}
                    >
                        {t('Preview')}
                    </XtramButton>
                    <XtramButton
                        variant="primary"
                        onClick={missingIncomplete ? () => setTab('missing') : handleCreateAndCommit}
                        disabled={!hasPreview || processing || isPreviewStale}
                        busy={processing}
                    >
                        {t('CreateAndImport')}
                    </XtramButton>
                </>
            )}
        >
            {isPreviewStale && (
                <div className={styles.section}>
                    <XtramNotice type="warning">
                        {previewedSkipDuplicates !== skipDuplicates
                            ? t('BmkvdPreviewOptionsChangedNotice')
                            : t('BmkvdDateRangeChangedNotice')}
                    </XtramNotice>
                </div>
            )}
            {activeTab === 'file' && renderFileTab()}
            {activeTab === 'summary' && hasPreview && renderSummaryTab()}
            {activeTab === 'reconciliations' && hasPreview && reconciliations && renderReconciliationsTab()}
            {activeTab === 'missing' && hasPreview && renderMissingTab()}
            {activeTab === 'cards' && hasPreview && renderCardsTab()}
            {activeTab === 'entries' && hasPreview && renderEntriesTab()}
        </XtramModal>
    );
};

export default BmkvdJournalImportModal;
